import { get } from 'lodash';

import fetchAction from 'utils/fetchAction';

import { wa } from 'apps/analytics';
import { config } from 'api';
import { ensureProducts } from 'apps/products/actions/products';
import checkProductWarnings from '../utils/checkProductWarnings';
import {
  showCouponNotification,
} from '../utils/showCouponNotification';
import {
  triggerGtmAddToBasket,
  triggerGtmChangeBasket,
  triggerGtmClearBasket,
  triggerCouponsAnalytics,
} from '../utils/basketAnalytics';

import Locations from 'apps/basket/models/Locations';

import { isCitrusAdProvider } from 'apps/analytics/utils/citrusAd';
import CitrusAdTracker from 'apps/analytics/services/CitrusAdTracker';

const BASKET_URL = `${config.apiBasePath}/basket`;

export const FETCH_BASKET_REQUEST = 'FETCH_BASKET_REQUEST';
export const FETCH_BASKET_SUCCESS = 'FETCH_BASKET_SUCCESS';
export const FETCH_BASKET_ERROR = 'FETCH_BASKET_ERROR';

export const UPDATE_BASKET_REQUEST = 'UPDATE_BASKET_REQUEST';
export const UPDATE_BASKET_SUCCESS = 'UPDATE_BASKET_SUCCESS';
export const UPDATE_BASKET_ERROR = 'UPDATE_BASKET_ERROR';

export const CLEAR_BASKET_REQUEST = 'CLEAR_BASKET_REQUEST';
export const CLEAR_BASKET_SUCCESS = 'CLEAR_BASKET_SUCCESS';
export const CLEAR_BASKET_ERROR = 'CLEAR_BASKET_ERROR';
export const CLEAR_PRODUCT_WARNING_MESSAGE = 'CLEAR_PRODUCT_WARNING_MESSAGE';

const getProductData = (fopData) => ({
  price: get(fopData, 'product.price.current'),
  sku: get(fopData, 'product.sku'),
  offerId: get(fopData, 'product.offer.id'),
  externalAdData: fopData.externalAdData,
});

const onUpdateBasketSuccess = ({ data, actionParams: { analyticsContextData } }) => () => {
  const successfullyUpdatedSkus = get(data, 'updateResults.SUCCESS');

  if (successfullyUpdatedSkus) {
    const additionalFopData = getProductData(analyticsContextData);
    const { externalAdData } = additionalFopData;
    const totalPriceChange = get(data, 'analyticsData.totalPriceChange');

    triggerGtmAddToBasket(successfullyUpdatedSkus);

    successfullyUpdatedSkus.forEach(successfullyUpdatedSku => {
      wa.triggerBasketQuantityUpdateEvent(
        [{ ...successfullyUpdatedSku, ...additionalFopData }], analyticsContextData
      );
    });

    if (externalAdData && isCitrusAdProvider(externalAdData.provider) && totalPriceChange > 0) {
      CitrusAdTracker.reportClick(externalAdData);
    }
  }
};

const basketTransformer = (basket) => basket.items.map((item) => ({
  analyticsData: {
    basketValueChange: -item.totalPrice,
    newTotalQuantity: 0,
    quantityChange: -item.quantity,
  },
  offerId: get(item, 'offer.id'),
  price: item.pricePerUnit,
  sku: item.sku,
}));

const onClearBasketSuccess = ({ actionParams: { basket } }) => () => {
  const analyticsContextData = {
    location: 'TROLLEY',
    updateFunction: 'EMPTY_BASKET',
  };

  if (basket) {
    const successfullyUpdatedSkus = basketTransformer(basket);
    if (successfullyUpdatedSkus) {
      triggerGtmAddToBasket(successfullyUpdatedSkus);
      wa.triggerBasketQuantityUpdateEvent(successfullyUpdatedSkus, analyticsContextData);
    }
  }
};

export const fetchBasket = fetchAction({
  actions: [
    FETCH_BASKET_REQUEST,
    FETCH_BASKET_SUCCESS,
    FETCH_BASKET_ERROR,
  ],
  url: BASKET_URL,
});

const fetchBasketWithCouponsUpdate = fetchAction({
  actions: [
    FETCH_BASKET_REQUEST,
    FETCH_BASKET_SUCCESS,
    FETCH_BASKET_ERROR,
  ],
  url: BASKET_URL,
  onSuccess: [
    showCouponNotification,
    triggerCouponsAnalytics,
  ],
});

const updateBasket = fetchAction({
  actions: [
    UPDATE_BASKET_REQUEST,
    UPDATE_BASKET_SUCCESS,
    UPDATE_BASKET_ERROR,
  ],
  url: `${BASKET_URL}/items`,
  isUserAction: true,
  method: 'patch',
  onSuccess: [
    onUpdateBasketSuccess,
    fetchBasketWithCouponsUpdate,
    triggerGtmChangeBasket,
  ],
});

/**
 * Params:
 * @param {Object[]} items Object where sku is key and quantity is value
 * @param {String} location Place where items where added like fop list or trolley etc.
 * @param {Object} analyticsContextData Analytics
 * @param {Object[]} enrichedItems For batched updates and cases where trolley is open - details about added fops (with section etc.)
 */
export function changeBasketQuantity({ items, location, analyticsContextData, enrichedItems }) {
  return (dispatch, getState, plugins) => {
    const parentContainer = (() => {
      switch (location) {
        case Locations.MINI_TROLLEY:
        case Locations.TROLLEY_SIDEBAR:
          return 'BASKET';
        default:
          return location;
      }
    })();
    const body = {
      items,
      parentContainer,
      additionalData: {},
    };

    const searchTerm = get(analyticsContextData, 'productsPageRouteLocationQuery.entry');
    if (searchTerm) {
      body.additionalData = { searchTerm, ...body.additionalData };
    }

    const externalAdData = get(analyticsContextData, 'externalAdData');
    if (externalAdData) {
      body.additionalData = { externalAdData, ...body.additionalData };
    }

    const state = getState();
    const { basket } = state;

    return updateBasket(
      { body },
      { analyticsContextData, basket, enrichedItems, getState }
    )(dispatch, getState, plugins)
      .then(() => checkProductWarnings(dispatch, getState, location));
  };
}

const clearBasketAction = fetchAction({
  actions: [
    CLEAR_BASKET_REQUEST,
    CLEAR_BASKET_SUCCESS,
    CLEAR_BASKET_ERROR,
  ],
  url: `${BASKET_URL}/`,
  isUserAction: true,
  method: 'del',
  onSuccess: [
    onClearBasketSuccess,
    fetchBasketWithCouponsUpdate,
    triggerGtmClearBasket,
  ],
});

export function clearBasket() {
  return (dispatch, getState, plugins) => {
    const state = getState();
    const { basket } = state;

    return clearBasketAction(
      null,
      { basket, getState }
    )(dispatch, getState, plugins);
  };
}

export function ensureBasketProducts() {
  return (dispatch, getState) => {
    const state = getState();
    const skusReq = state.basket.items
      .filter(item => (!state.products.productsBySku[item.sku]))
      .map(item => ({ sku: item.sku }));
    return dispatch(ensureProducts(skusReq));
  };
}

export function clearProductWarning(sku) {
  return (dispatch) => {
    dispatch({
      type: 'CLEAR_PRODUCT_WARNING_MESSAGE',
      sku,
    });
  };
}
